import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit'
import { AxiosError } from 'axios'

import { CostPriceApiResponse, CostPriceReducerPayload, CostPriceState } from './costprice.types'
import { RootState } from '../../store/rootReducer'
import apiClient from '../../services/api-client'
import { storageDb } from '../../services/localforage'

export const getCostPrice = createAsyncThunk<
  CostPriceReducerPayload,
  { productId: number; accountNumber: string }
>('costPrice/getCostPrice', async ({ productId, accountNumber }) => {
  const request = await apiClient.get<CostPriceApiResponse, AxiosError>(
    `products/${productId}/cost-prices?accountNumber=${accountNumber}`,
  )

  return {
    breaks: request.data.costPrices,
    productId,
  }
})

export const costPricePersistConfig = {
  key: 'costPrice',
  storage: storageDb,
  // serialize: false,
  blacklist: ['status'],
}

export const initialState: CostPriceState = {
  dictionary: {},
  status: {},
}

const setStatus = (
  state: CostPriceState,
  productId: number,
  status: 'idle' | 'pending' | 'fulfilled',
) => {
  state.status = {
    ...state.status,
    [productId]: {
      status: status,
      date: new Date().toUTCString(),
    },
  }
}

const CostPriceSlice = createSlice({
  name: 'costPrice',
  initialState: initialState,
  extraReducers: (builder) => {
    builder.addCase(getCostPrice.pending, (state, { meta }) => {
      setStatus(state, meta.arg.productId, 'pending')
    })
    builder.addCase(getCostPrice.fulfilled, (state, { payload, meta }) => {
      setStatus(state, meta.arg.productId, 'fulfilled')
      state.dictionary = {
        ...state.dictionary,
        [meta.arg.productId]: payload.breaks,
      }
    })
    builder.addCase(getCostPrice.rejected, (state, { meta }) => {
      setStatus(state, meta.arg.productId, 'idle')
    })
  },
  reducers: {},
})

const selectState = (state: RootState) => state.costPrice

export const selectCostPriceById = (id: number) => {
  return createSelector(selectState, (state) => {
    if (state.dictionary[id]) {
      const costBreakpoints = state.dictionary[id]
      return costBreakpoints
    }
  })
}

export const selectCostPriceStatus = (id: number) => {
  return createSelector(selectState, (state) => {
    if (state.status[id]) {
      const status = state.status[id]
      return status.status
    }

    return 'idle'
  })
}

export type { CostPriceState } from './costprice.types'
export default CostPriceSlice
